import Input from '@/components/Input.jsx';
import Panel from '@/components/panel/Panel.jsx';
import { BiSave, BiText, MdMemory, MdOutlinePublic, RxLapTimer } from '@/components/react-icons/main';
import debounce from '@/lib/debounce.js';
import sendToast from '@/lib/sendToast.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import ArgumentLabel from '../ArgumentLabel.jsx';
import ArgumentWrapper from '../ArgumentWrapper.jsx';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';
import { memoryExpiresOnOptions, memorySavesOnOptions, memoryScopeOptions, embeddedMemoryScopeOptions } from '@shared/schema/aiSchemas/passive/workflowSchemas/memorySchema.js';
import useMemoryKeyInputValidation from '@/hooks/useMemoryKeyInputValidation.js';
import { openDocDrawer } from '@/redux/actions/uiActions/drawerActions.js';

const MemorySlider = ({ onClose, disabled = false }) => {
  const isEditingRef = useRef(false);

  const usesEnvironments = useSelector(workflowScreenSelectors.activeWorkflowUsesEnvironments);
  const isEmbedded = useSelector(workflowScreenSelectors.activeWorkflowIsEmbedded);
  const scopeOptions = isEmbedded ? embeddedMemoryScopeOptions : memoryScopeOptions;
  const activeStep = useSelector(workflowScreenSelectors.activeWorkflowStep);
  const memory = activeStep?.options?.memory || [];
  const stepId = activeStep?.id;
  const memoryId = useSelector((state) => state?.ui?.argumentOptionSlider?.memoryId);
  const currentMemory = memory.find((m) => m.id === memoryId) || {};
  const [localMemory, setLocalMemory] = useState(currentMemory);
  const localMemoryRef = useRef(currentMemory);
  const { memoryKey, memoryKeyDescription, invalidKey } = useMemoryKeyInputValidation({ key: localMemory.key, scope: localMemory.scope });

  const workflowScopeValue = localMemory.scope || 'workflow';
  const workflowScopeInfo = 'It is recommended you set the scope to "This Environment".';

  useEffect(() => {
    if (!isEditingRef.current) {
      setLocalMemory(currentMemory);
      localMemoryRef.current = currentMemory;
    }
  }, [memoryId, currentMemory]);

  const getLabel = (value, options) => {
    const option = options.find((o) => o.value === value);
    return option?.label || value;
  };

  const updateMemory = useMemo(() => {
    return debounce((updatedMemory) => {
      stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).map((option) => (option.id === memoryId ? { ...option, ...updatedMemory } : option)), { stepId });
      isEditingRef.current = false;
    }, 300);
  }, [memoryId, stepId]);

  const handleChange = (field, value) => {
    if (field !== 'key' && !localMemoryRef.current.key) {
      sendToast({ message: 'Please enter a key for this memory', type: 'error' });
      return;
    }

    const updatedMemory = { ...localMemoryRef.current, [field]: value };
    isEditingRef.current = true;
    localMemoryRef.current = updatedMemory;
    setLocalMemory(updatedMemory);
    updateMemory(updatedMemory);
  };

  const handleDelete = async () => {
    stepActions.set('options.memory', (options) => (Array.isArray(options) ? options : []).filter((option) => option.id !== memoryId));
    onClose();
  };

  const handleLearnMore = () => {
    openDocDrawer({ docId: 'workflowStepMemory', highlightedElementId: undefined });
    onClose();
  };

  return (
    <>
      <SliderHeader onClose={onClose} showDelete onDelete={handleDelete} disabled={disabled} />
      <SliderTitle icon={MdMemory} title="Edit Memory" description="Configure memory settings for this entry." helpIconContext="workflowStepMemoryContext" />
      <Panel.Section title="Key" collapsable={false}>
        <Input disabled={disabled} value={memoryKey} className={invalidKey ? 'error' : ''} onChange={(e) => handleChange('key', e.target.value)} placeholder="Enter memory key..." description={memoryKeyDescription} />
      </Panel.Section>
      <Panel.Section title="Options" className={!localMemory.key ? 'disabled' : ''} collapsable={false}>
        <Panel.LexicalInput //
          label="Value"
          icon={BiText}
          value={localMemory.value}
          onChange={(value) => handleChange('value', value)}
          placeholder="Set a Value"
          disabled={disabled || !localMemory.key}
        />
        <ArgumentWrapper>
          <ArgumentLabel label="Scope" icon={MdOutlinePublic} />
          <Panel.ToggleButton options={scopeOptions} value={workflowScopeValue} onChange={(value) => handleChange('scope', value)} disabled={disabled || !localMemory.key} fullWidth size="sm" />
          {usesEnvironments && workflowScopeValue === 'workflow' && (
            <Panel.Alert
              title={
                <>
                  {workflowScopeInfo} <br />
                  <span className="learn-more" onClick={handleLearnMore}>
                    Learn more.
                  </span>
                </>
              }
              variant={'info'}
            />
          )}
        </ArgumentWrapper>
        <ArgumentWrapper>
          <ArgumentLabel label="Saves On" icon={BiSave} />
          <Panel.ToggleButton options={memorySavesOnOptions} value={localMemory.savesOn || 'success'} onChange={(value) => handleChange('savesOn', value)} disabled={disabled || !localMemory.key} fullWidth size="sm" />
        </ArgumentWrapper>
        <Panel.Select label="Expires After" icon={RxLapTimer} value={getLabel(localMemory.expiresOn || 'never', memoryExpiresOnOptions)} onChange={({ value }) => handleChange('expiresOn', value)} placeholder="Select when the value will expire" options={memoryExpiresOnOptions} disabled={disabled || !localMemory.key} />
      </Panel.Section>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default MemorySlider;
